import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiClient } from "../../api/client";
import { ClientsListParams, ClientItem, StatesListResponse } from "../../api/types";
import { parseApiError } from "../../utils/errors/errorParser";
import { AxiosError } from "axios";

interface ClientsState {
  clients: ClientItem[];
  states: StatesListResponse;
  meta: {
    total: number;
    page: number;
    page_size: number;
    pages: number;
  };
  filters: {
    page: number;
    page_size: number;
    query: string | null;
    orders?: string[];
  };
  loading: boolean;
  statesLoading: boolean;
  error: string | null;
}

const initialState: ClientsState = {
  clients: [],
  states: [],
  meta: {
    total: 0,
    page: 1,
    page_size: 20,
    pages: 0,
  },
  filters: {
    page: 1,
    page_size: 20,
    query: null,
  },
  loading: false,
  statesLoading: false,
  error: null,
};

export const fetchClients = createAsyncThunk(
  "clients/fetchClients",
  async (params: ClientsListParams | undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.getClients(params);
      return response;
    } catch (error) {
      const err = parseApiError(error as AxiosError);
      return rejectWithValue(err.message);
    }
  }
);

export const fetchStates = createAsyncThunk(
  "clients/fetchStates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.getStates();
      return response;
    } catch (error) {
      const err = parseApiError(error as AxiosError);
      return rejectWithValue(err.message);
    }
  }
);

const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filters.page_size = action.payload;
      state.filters.page = 1;
    },
    setSearchQuery: (state, action: PayloadAction<string | null>) => {
      state.filters.query = action.payload;
      state.filters.page = 1;
    },
    setOrders: (state, action: PayloadAction<string[]>) => {
      state.filters.orders = action.payload;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload.items;
        state.meta = action.payload.meta;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchStates.pending, (state) => {
        state.statesLoading = true;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.statesLoading = false;
        state.states = action.payload;
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.statesLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setSearchQuery,
  setOrders,
  clearFilters,
  clearError,
} = clientsSlice.actions;

export default clientsSlice.reducer;